import * as THREE from "three";
import { HqMaterials } from "../../hqMaterials";
import { KIT_BUDGET, type Motion } from "./types";
import { FLAP_AXES, FLOW_AXES } from "./bigTypes";
import { KitClock } from "./clock";
import { COPY_STRIDE } from "./kitGeometry";
import { KIT_MOTION_GLSL } from "./motionGlsl";

/** Defaults for a motion's unset fields. */
export const MOTION_DEFAULTS = { hz: 0.5, waveK: 18 } as const;
/** Rows of the GPU motion table per use (vec4s): see `packMotion`. */
export const MOTION_ROWS = 7;

/**
 * One use's motion, packed for the GPU table:
 *  A [spin, bob, sway rad, pulse]          B [hz, wave, waveK, glow]          C [twinkle, detail, roll, travel]
 *  D [orbit on, orbit rad/s, orbit tilt rad, orbit copies]                   E [flap rad, flap bias rad, flap hinge m, flap mode]
 *  F [flow m/s, flow length m, flow glow, flow bulge m]                      G [flow axis, charge glow, charge pulse, flap hz]
 * Flap mode = axis index (FLAP_AXES: out, right, up) + 4 when mirrored + 8 when driven by the charge (the strike,
 * `uKitStrike`, replaces the sine); flow axis = FLOW_AXES index. The orbit's
 * radius is baked into the geometry (kitGeometry.ts: the piece built that far out along its out axis), so a fitted
 * piece's orbit follows each tower's own envelope.
 */
export function packMotion(m: Motion | undefined, detail: boolean): THREE.Vector4[] {
  const x = m ?? {};
  const rad = THREE.MathUtils.degToRad;
  const o = x.orbit, f = x.flap, w = x.flow, c = x.charge;
  const hz = x.hz ?? MOTION_DEFAULTS.hz;
  return [
    new THREE.Vector4(x.spin ?? 0, x.bob ?? 0, rad(x.sway ?? 0), x.pulse ?? 0),
    new THREE.Vector4(hz, x.wave ?? 0, x.waveK ?? MOTION_DEFAULTS.waveK, x.glow ?? 0),
    new THREE.Vector4(x.twinkle ?? 0, detail ? 1 : 0, x.roll ?? 0, x.travel ?? 0),
    new THREE.Vector4(o ? 1 : 0, o?.speed ?? 0, rad(o?.tilt ?? 0), o ? Math.max(1, Math.round(o.copies ?? 1)) : 1),
    new THREE.Vector4(rad(f?.deg ?? 0), rad(f?.bias ?? 0), f?.hinge ?? 0, FLAP_AXES.indexOf(f?.axis ?? "out") + ((f?.mirror ?? (f?.axis ?? "out") === "out") ? 4 : 0) + (f?.drive === "charge" ? 8 : 0)),
    new THREE.Vector4(w?.speed ?? 0, Math.max(1e-3, w?.length ?? 1), w?.glow ?? 0, w?.bulge ?? 0),
    new THREE.Vector4(FLOW_AXES.indexOf(w?.along ?? "up"), c?.glow ?? 0, c?.pulse ?? 0, f?.hz ?? hz),
  ];
}

/** Whether any row answers the tower's attack — a charge, or a flap driven by it (it then needs a material per tower to carry its charge). */
export function rowsCharge(rows: readonly { motion?: Motion }[]): boolean {
  return rows.some((r) => (r.motion?.charge?.glow ?? 0) !== 0 || (r.motion?.charge?.pulse ?? 0) !== 0 || r.motion?.flap?.drive === "charge");
}

/**
 * The attachments' material: the kit GLB's own converted material (its atlas, emissive, PBR maps; HqMaterials.convert
 * gave it the game's lighting hooks) plus the ambient motion, evaluated per vertex BEFORE skinning so a prop rides its
 * part through every clip and moves on top of that. One program per kit; the motion of each use is a row of a small
 * uniform table (`MaxUses` × MOTION_ROWS vec4), so all the props of a tower are one draw.
 *
 * Per vertex (kit/kitGeometry.ts writes them): `kitPivot` the mount point, `kitUp` / `kitOut` the socket's axes (in
 * the bone's space, their length the bone's metre-to-local factor, so amplitudes are authored in metres), `kitData`
 * = (use row + orbit copy × 16, cloth weight). The instanced variant (road props, KIT_INSTANCED) mounts each piece at
 * its own origin. A kit with a `charge` row gets a clone per dressed tower (`perTower`): same program, its own
 * `uKitCharge` (and `uKitStrike`, a charge-driven flap's angle).
 */
export class KitMaterials {
  static readonly MaxUses = KIT_BUDGET.UsesPerKit;
  private static readonly variants = new Map<THREE.Material, Map<string, THREE.MeshStandardMaterial>>();
  /** Per variant: its uniforms and compile hook, so a per-tower clone can share the program with its own charge. */
  private static readonly hooks = new WeakMap<THREE.Material, (u: Record<string, THREE.IUniform>) => THREE.Material["onBeforeCompile"]>();
  private static readonly shared = new WeakMap<THREE.Material, Record<string, THREE.IUniform>>();

  /** The variant of `source` for the kit `key` with one motion row per use (and detail flags). */
  static variant(source: THREE.MeshStandardMaterial, key: string, rows: readonly { motion?: Motion; detail?: boolean }[], instanced = false): THREE.MeshStandardMaterial {
    const k = `${key}${instanced ? "#i" : ""}`;
    let byKey = KitMaterials.variants.get(source);
    if (!byKey) { byKey = new Map(); KitMaterials.variants.set(source, byKey); }
    let v = byKey.get(k);
    if (v) return v;
    v = source.clone();
    v.name = `${source.name}~${k}`;
    v.vertexColors = false;                 // COLOR_0 is the cloth weight, not a colour (GLTFLoader turned this on)
    const N = KitMaterials.MaxUses;
    const table: THREE.Vector4[][] = Array.from({ length: MOTION_ROWS }, () => []);
    for (let i = 0; i < N; i++) packMotion(rows[i]?.motion, rows[i]?.detail === true).forEach((r, j) => table[j].push(r));
    const u: Record<string, THREE.IUniform> = {
      uKitA: { value: table[0] }, uKitB: { value: table[1] }, uKitC: { value: table[2] }, uKitD: { value: table[3] }, uKitE: { value: table[4] },
      uKitF: { value: table[5] }, uKitG: { value: table[6] },
      uKitTime: KitClock.time, uKitMotion: KitClock.motion, uKitDetail: KitClock.detail, uKitCharge: { value: 0 }, uKitStrike: { value: 0 },
    };
    const hook = (uniforms: Record<string, THREE.IUniform>): THREE.Material["onBeforeCompile"] => (sh, r) => {
      source.onBeforeCompile.call(source, sh, r);
      Object.assign(sh.uniforms, uniforms);
      sh.vertexShader = sh.vertexShader
        .replace("#include <common>", `#include <common>\n${instanced ? "#define KIT_INSTANCED" : ""}\n${KIT_MOTION_GLSL.head(N, COPY_STRIDE)}`)
        .replace("#include <beginnormal_vertex>", `#include <beginnormal_vertex>\n${KIT_MOTION_GLSL.body}`)
        .replace("#include <begin_vertex>", "#include <begin_vertex>\n  transformed = tdKitPos;");
      sh.fragmentShader = sh.fragmentShader
        .replace("#include <common>", "#include <common>\nvarying float vKitGlow;")
        .replace("#include <emissivemap_fragment>", "#include <emissivemap_fragment>\n  totalEmissiveRadiance *= vKitGlow;");
    };
    v.onBeforeCompile = hook(u);
    const key0 = source.customProgramCacheKey();
    v.customProgramCacheKey = () => `${key0}-kitm${instanced ? "i" : ""}`;
    KitMaterials.hooks.set(v, hook);
    KitMaterials.shared.set(v, u);
    const entry = HqMaterials.standard.find((e) => e.mat === source);
    HqMaterials.standard.push({ mat: v, ground: entry?.ground ?? false });
    byKey.set(k, v);
    return v;
  }

  /**
   * A dressed tower's own copy of a charge kit's variant: the same program (same cache key, same tables), its own
   * `uKitCharge` and `uKitStrike` (returned) that kit/charge.ts writes each frame. Freed with `release` when the tower
   * is undressed.
   */
  static perTower(variant: THREE.MeshStandardMaterial): { material: THREE.MeshStandardMaterial; charge: { value: number }; strike: { value: number } } {
    const hook = KitMaterials.hooks.get(variant), u = KitMaterials.shared.get(variant);
    if (!hook || !u) return { material: variant, charge: { value: 0 }, strike: { value: 0 } };
    const charge = { value: 0 }, strike = { value: 0 };
    const m = variant.clone();
    m.name = `${variant.name}~tower`;
    m.onBeforeCompile = hook({ ...u, uKitCharge: charge, uKitStrike: strike });
    m.customProgramCacheKey = variant.customProgramCacheKey;
    m.userData.tdKitPerTower = true;
    return { material: m, charge, strike };
  }

  /** Frees a per-tower copy (its program stays: the variant and the other towers share it). */
  static release(m: THREE.Material): void { if (m.userData.tdKitPerTower === true) m.dispose(); }

  static forget(mats: ReadonlySet<THREE.Material>): THREE.Material[] {
    const gone: THREE.Material[] = [];
    for (const m of mats) {
      const byKey = KitMaterials.variants.get(m);
      if (!byKey) continue;
      for (const v of byKey.values()) gone.push(v);
      KitMaterials.variants.delete(m);
    }
    return gone;
  }
}
HqMaterials.forgetHooks.push((mats) => KitMaterials.forget(mats));
